import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { clamp } from 'lodash';
import { useIntl } from 'react-intl';

import {
  getHomePageUrl,
  getOnboardingEditReportUrl,
  getReportingEditReportUrl,
  getOnboardingReportingUrl,
  matchOnboardingEditReportUrl
} from '@futrli/shared-services';
import { LiveReportDetail } from '@futrli-sdk/predict';

import { createContext } from '../core/context';
import { deleteLiveReport } from '../core/api/reporting/report-builder';
import { ONBOARDING_STEPS, OnboardingStepText } from './types';

interface OnboardingContextData {
  currentStep: number;
  handleEnd: () => void;
  handleNext: (createdReport?: LiveReportDetail) => void;
  isNextEnabled: boolean;
  progress: number;
  setIsNextEnabled: (enabled: boolean) => void;
  stepText?: OnboardingStepText;
}

interface OnboardingContextProps {
  children?: ReactNode;
}

const OnboardingContext = createContext<OnboardingContextData>();

const TOTAL_STEPS = 6;

const getLocalStorageData = () => ({
  storedReportId: (localStorage.getItem('onboarding_report_id') || '').trim(),
  storedStep: (localStorage.getItem('onboarding_report_step') || '1').trim()
});

const useContextSetup = () => {
  const {
    workspaceId: tempWorkspaceId,
    organisationId: tempOrganisationId,
    reportId: tempReportId,
    stepId: tempStepId
  } = useParams();
  const workspaceId = tempWorkspaceId || '';
  const organisationId = tempOrganisationId || '';
  const reportId = tempReportId || '';
  const stepId = tempStepId || '';
  const currentStep: ONBOARDING_STEPS = clamp(parseInt(stepId, 10) || 1, 1, TOTAL_STEPS);
  const navigate = useNavigate();
  const intl = useIntl();
  const progress = (currentStep * 100) / TOTAL_STEPS;
  const [isNextEnabled, setIsNextEnabled] = useState(false);
  const [stepText, setStepText] = useState<OnboardingStepText>();

  const TEXT: Record<string, OnboardingStepText[]> = useMemo(
    () => ({
      [ONBOARDING_STEPS.ADD_TEMPLATE]: [
        {
          title: intl.formatMessage({
            id: 'reporting.onboarding_title_1',
            defaultMessage: 'Drag and drop the template onto your page...'
          }),
          desc: intl.formatMessage({
            id: 'reporting.onboarding_desc_1',
            defaultMessage: "We'll show you how quickly a report can be made and customised."
          })
        }
      ],
      [ONBOARDING_STEPS.ADD_WIDGET]: [
        {
          title: intl.formatMessage({
            id: 'reporting.onboarding_title_2',
            defaultMessage: 'There are pre-made widgets for you to add...'
          }),
          desc: intl.formatMessage({
            id: 'reporting.onboarding_desc_2',
            defaultMessage: 'Drop one onto your page and see your data brought to life.'
          })
        }
      ],
      [ONBOARDING_STEPS.SELECT_WIDGET]: [
        {
          title: intl.formatMessage({
            id: 'reporting.onboarding_title_3',
            defaultMessage: 'Select this chart to view the builder...'
          }),
          desc: intl.formatMessage({
            id: 'reporting.onboarding_desc_3',
            defaultMessage:
              'All of your historical data (and future when you build a forecast or budget), is available for reports.'
          })
        },
        {
          title: intl.formatMessage({
            id: 'reporting.onboarding_title_4',
            defaultMessage: 'Change a chart or drag Income to the x-axis'
          }),
          desc: intl.formatMessage({
            id: 'reporting.onboarding_desc_4',
            defaultMessage:
              'You can see how this widget has been built with actual data, prior year data, gross profit% and months as the time period.'
          })
        }
      ],
      [ONBOARDING_STEPS.ADD_TEXT_IMAGES]: [
        {
          title: intl.formatMessage({
            id: 'reporting.onboarding_title_5',
            defaultMessage: 'Add text and images to your reports...'
          }),
          desc: intl.formatMessage({
            id: 'reporting.onboarding_desc_5',
            defaultMessage:
              "There are pre-built executive summary and title page templates when you've completed onboarding."
          })
        }
      ],
      [ONBOARDING_STEPS.SEND_OR_SCHEDULE]: [
        {
          title: intl.formatMessage({
            id: 'reporting.onboarding_title_6',
            defaultMessage: "And when you're ready...send or schedule your report"
          }),
          desc: intl.formatMessage({
            id: 'reporting.onboarding_desc_6',
            defaultMessage:
              'Your data updates daily from your accounts package, so get your reports scheduled straight into your inbox.'
          })
        }
      ],
      [ONBOARDING_STEPS.SEE_HELP]: [
        {
          title: intl.formatMessage({
            id: 'reporting.onboarding_title_7',
            defaultMessage: "You're ready to go! Learn more with our help section here..."
          }),
          desc: intl.formatMessage({
            id: 'onboarding.start_with_futrli_now',
            defaultMessage: 'Get started with Futrli now!'
          })
        }
      ]
    }),
    [intl]
  );

  const handleEnd = useCallback(async () => {
    const { storedReportId } = getLocalStorageData();

    // If we have a onboarding report id in localStorage, attempt to delete that report
    if (storedReportId) {
      try {
        await deleteLiveReport(workspaceId, organisationId, storedReportId);
      } catch (e) {
        // If deleting the onboarding report fails, just continue...
      }
    }

    localStorage.removeItem('onboarding_report_id');
    localStorage.removeItem('onboarding_report_step');
    navigate(getHomePageUrl(workspaceId, organisationId));
  }, [navigate, organisationId, workspaceId]);

  const handleNext = useCallback(
    (createdReport?: LiveReportDetail) => {
      const id = createdReport?.id || reportId;
      const nextStep = createdReport?.id ? 1 : currentStep + 1;

      if (createdReport?.id) {
        localStorage.setItem('onboarding_report_id', createdReport.id);
      }

      if (nextStep > TOTAL_STEPS) {
        localStorage.removeItem('onboarding_report_id');
        localStorage.removeItem('onboarding_report_step');
        navigate(getReportingEditReportUrl(workspaceId, organisationId, id));
      } else {
        localStorage.setItem('onboarding_report_step', nextStep.toString());
        navigate(getOnboardingEditReportUrl(workspaceId, organisationId, id, nextStep));
      }
    },
    [currentStep, navigate, organisationId, reportId, workspaceId]
  );

  // Set the stepText variable when current step / currtent step completed status changes
  useEffect(() => {
    switch (currentStep) {
      case ONBOARDING_STEPS.SELECT_WIDGET: {
        setStepText(TEXT[ONBOARDING_STEPS.SELECT_WIDGET][isNextEnabled ? 1 : 0]);
        break;
      }

      default: {
        setStepText(TEXT[currentStep]?.[0]);
      }
    }
  }, [currentStep, isNextEnabled, TEXT]);

  // If the id in localStorage doesn't match the id in the URL, redirect
  useEffect(() => {
    const isOnboardingRoute = !!matchOnboardingEditReportUrl(location.pathname);
    const { storedReportId, storedStep } = getLocalStorageData();

    if (isOnboardingRoute) {
      if (reportId && storedReportId !== reportId) {
        if (storedReportId) {
          navigate(getOnboardingEditReportUrl(workspaceId, organisationId, storedReportId, storedStep));
        } else {
          navigate(getOnboardingReportingUrl(workspaceId, organisationId));
        }
      }
    }
  }, [navigate, organisationId, reportId, workspaceId]);

  return {
    currentStep,
    handleEnd,
    handleNext,
    isNextEnabled,
    progress,
    setIsNextEnabled,
    stepText
  };
};

export const OnboardingContextProvider = ({ children }: OnboardingContextProps) => {
  const value = useContextSetup();
  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
};

export const useOnboardingContext = OnboardingContext.useContext;
